/*
 * ******************************************************
 * Copyright (c) 2022 Jenke & Rauschert GbR <info@aiandor.de> - All Rights Reserved
 *
 * This file is part of a software product owned by the Jenke & Rauschert GbR.
 * Unauthorized distribution of this file is strictly prohibited.
 * ******************************************************
 */

import dayjs from "dayjs";
import 'dayjs/locale/de';

export enum Gender {
    'unkonwn' = 0,
    'male' = 1,
    'female' = 2,
    'divers' = 3,
    'other' = 9,
}

/*
removed 1.1.2025; we fetch available software from the backemd
export enum Software {
    "Unknown",
    "Ashvins",
    "Domako",
    "DoseWatch",
    "DoseWise",
    "Excel",
    "Teamplay",
    "TQM-Dose",
    "Dose&Care",
    "RadCentre",
    "DoseM",
    "Domako (3rd Party)",
}
*/

export enum CRDX {
    'Unknown',
    'CR',
    'DX',
}

export enum DeviceType {
    'CT',
    'XR',
    'MG',
    'RF',
    'NM',
    'PET/CT',
}

// ORMObject
interface ORMObjectData {
    uuid?: string,
}

interface ORMObjectProps {
    uuid?: string,
    repr?: string,
}

export abstract class ORMObject {
    abstract props: { [key: string]: any }

    update(props: { [key: string]: any }) {
        Object.entries(props).forEach(([key, value]) => {
            console.debug(key, value, !!value || value === 0, (!!value || value === 0) ? value : null)
            this.props[key] = (!!value || value === 0) ? value : null
        })
    }

    abstract dump(): { [key: string]: any }
}

// Person
interface PersonData extends ORMObjectData {
    firstname?: string,
    surname?: string,
    gender?: Gender,
    title?: string,
    mail?: string,
    phone?: string,
    mobile?: string,
    comment?: string,
    salutation?: string,
    is_deletable?: boolean,
}

interface PersonProps extends ORMObjectProps {
    disabled?: boolean,
    firstname?: string,
    surname?: string,
    gender?: Gender,
    title?: string,
    mail?: string,
    phone?: string,
    mobile?: string,
    comment?: string,
    salutation?: string,
    is_deletable?: boolean,
}

export class Person extends ORMObject {
    props: PersonProps

    constructor(props: PersonData) {
        super();
        this.props = {
            ...props,
        } as PersonProps
    }

    public dump(): PersonData {
        return {
            ...this.props,
        };
    }
}

// Customer
interface CustomerData extends ORMObjectData {
    disabled?: boolean,
    name?: string,
    alias?: string,
    comment?: string,
    synologylink?: string,
    repr?: string,
    street?: string,
    additional_adresse?: string,
    house_nr?: string,
    postcode?: string,
    city?: string,
    contact_invoice?: PersonData | null,
    contact_ceo?: PersonData | null,
    contact_it?: PersonData | null,
    contact_radiation_protection_officer?: PersonData | null,
    mpe?: PersonData | null,
    is_deletable?: boolean,
}

interface CustomerProps extends ORMObjectProps {
    disabled?: boolean,
    name?: string,
    alias?: string,
    repr?: string,
    comment?: string,
    synologylink?: string,
    street?: string,
    house_nr?: string,
    additional_adresse?: string,
    postcode?: string,
    city?: string,
    contact_invoice?: Person,
    contact_ceo?: Person,
    contact_it?: Person,
    contact_radiation_protection_officer?: Person,
    mpe?: Person,
    is_deletable?: boolean,
}

export class Customer extends ORMObject {
    props: CustomerProps

    constructor(props: CustomerData) {
        super();
        this.props = {
            ...props,
            contact_ceo: props.contact_ceo ? new Person(props.contact_ceo) : undefined,
            contact_it: props.contact_it ? new Person(props.contact_it) : undefined,
            contact_invoice: props.contact_invoice ? new Person(props.contact_invoice) : undefined,
            contact_radiation_protection_officer: props.contact_radiation_protection_officer ?
                new Person(props.contact_radiation_protection_officer) : undefined,
            mpe: props.mpe ? new Person(props.mpe) : undefined,

        } as CustomerProps
    }

    public dump(): CustomerData {
        return {
            ...this.props,
            contact_ceo: this.props.contact_ceo ? {'uuid': this.props.contact_ceo.props.uuid} : null,
            contact_invoice: this.props.contact_invoice ? {'uuid': this.props.contact_invoice.props.uuid} : null,
            contact_it: this.props.contact_it ? {'uuid': this.props.contact_it.props.uuid} : null,
            contact_radiation_protection_officer: this.props.contact_radiation_protection_officer ?
                {'uuid': this.props.contact_radiation_protection_officer.props.uuid} : null,
            mpe: this.props.mpe ? {'uuid': this.props.mpe.props.uuid} : null,
        };
    }
}

// Device
export interface DeviceData extends ORMObjectData {
    disabled?: boolean,
    name?: string,
    alias?: string
    manufacture_date?: string | null,
    comment?: string,
    software?: string,
    synologylink?: string,
    notes_for_billing?: number,
    number_rows?: number,
    iterativerecon?: string,
    crdx?: CRDX,
    tomosynthese?: boolean,
    street?: string,
    additional_adresse?: string,
    house_nr?: string,
    postcode?: string,
    city?: string,
    contact_person_ids?: string[] | null,
    contact_persons?: PersonData[] | null,
    device_model?: DeviceModelData | null,
    customer?: CustomerData | null,
    is_deletable?: boolean,
    has_mappings?: boolean,
    drwmapping_upload_date?: string | null,
}

interface DeviceProps extends ORMObjectProps {
    disabled?: boolean,
    name?: string,
    alias?: string
    manufacture_date?: dayjs.Dayjs,
    comment?: string,
    software?: string,
    synologylink?: string,
    notes_for_billing?: number,
    number_rows?: number,
    iterativerecon?: string,
    crdx?: CRDX,
    tomosynthese?: boolean,
    street?: string,
    additional_adresse?: string,
    house_nr?: string,
    postcode?: string,
    city?: string,
    contact_person_ids?: string[],
    contact_persons?: Person[],
    device_model?: DeviceModel,
    customer?: Customer,
    type?: string,
    is_deletable?: boolean,
    has_mappings?: boolean,
    drwmapping_upload_date?: dayjs.Dayjs,
}

export class Device extends ORMObject {
    props: DeviceProps

    constructor(props: DeviceData) {
        super();
        this.props = {
            ...props,
            manufacture_date: props.manufacture_date ? dayjs(props.manufacture_date, "DD.MM.YYYY") : undefined,
            contact_persons: props.contact_persons ? props.contact_persons.map((p: PersonData) => new Person(p)) : undefined,
            device_model: props.device_model ? new DeviceModel(props.device_model) : undefined,
            customer: props.customer ? new Customer(props.customer) : undefined,
            drwmapping_upload_date: props.drwmapping_upload_date ? dayjs(props.drwmapping_upload_date, "DD.MM.YYYY hh:mm:ss") : undefined,
        } as DeviceProps
    }

    public dump(): DeviceData {
        return {
            ...this.props,
            manufacture_date: this.props.manufacture_date ? this.props.manufacture_date.format("DD.MM.YYYY") : null,
            device_model: this.props.device_model ? {'uuid': this.props.device_model.props.uuid} : null,
            customer: this.props.customer ? {'uuid': this.props.customer.props.uuid} : null,
            contact_persons: null,
            drwmapping_upload_date: this.props.drwmapping_upload_date ? this.props.drwmapping_upload_date.format("DD.MM.YYYY hh:mm:ss") : null,
        };
    }
}

// DeviceModel
interface DeviceModelData extends ORMObjectData {
    name?: string,
    type?: string,
    manufacturer?: string,
    is_deletable?: boolean,
}

interface DeviceModelProps extends ORMObjectProps {
    name?: string,
    type?: string,
    manufacturer?: string,
    is_deletable?: boolean,
}

export class DeviceModel extends ORMObject {
    props: DeviceModelProps

    constructor(props: DeviceModelData) {
        super();
        this.props = {
            ...props,
        } as DeviceModelProps
    }

    public dump(): DeviceModelData {
        return {
            ...this.props,
        };
    }
}

// User
interface UserData extends ORMObjectData {
    disabled?: boolean,
    username?: string,
    groups?: string[],
    password?: string
    person?: PersonData | null,
    is_deletable?: boolean,
}

interface UserProps extends ORMObjectProps {
    disabled?: boolean,
    username?: string,
    groups?: string[],
    password?: string,
    person?: Person,
    is_deletable?: boolean,
}

export class User extends ORMObject {
    props: UserProps

    constructor(props: UserData) {
        super();
        this.props = {
            ...props,
            person: props.person ? new Customer(props.person) : undefined,
        } as UserProps
    }

    public dump(): UserData {
        let dump_d = {
            ...this.props,
            person: this.props.person ? {'uuid': this.props.person.props.uuid} : null,
        }
        if (dump_d.password === undefined || dump_d.password === null) {
            delete dump_d.password;
        }

        return dump_d;
    }
}

//Event
export interface EventData extends ORMObjectData {
    exam: { uuid: string },
    acq_protocol: string,
    acq_type: string,
    acq_time: string | null,
    radlex_body_region: string,
    protocol: string,
    anat_region: string,
    rpid: number,
    phantom: number,
    pat_pos: string,
    is_deletable?: boolean,
}

interface EventProps extends ORMObjectProps {
    exam: { uuid: string },
    acq_protocol: string,
    acq_type: string,
    acq_time: dayjs.Dayjs,
    radlex_body_region: string,
    protocol: string,
    anat_region: string,
    rpid: number,
    phantom: number,
    pat_pos: string,
    is_deletable?: boolean,
}

//  CT
interface CTEventData extends EventData {
    pitch_fact: number,
    ssde: number,
    scan_length: number,
    scan_length_refInt: number,
    scan_length_refNat: number,
    scan_length_wRefInt: number,
    scan_length_wRefNat: number,

    ctdi: number,
    ctdi_refInt: number,
    ctdi_refNat: number,
    ctdi_wRefInt: number,
    ctdi_wRefNat: number,

    dlp: number,
    dlp_refInt: number,
    dlp_refNat: number,
    dlp_wRefInt: number,
    dlp_wRefNat: number,
}

interface CTEventProps extends EventProps {
    pitch_fact: number,
    ssde: number,
    scan_length: number,
    scan_length_refInt: number,
    scan_length_refNat: number,
    scan_length_wRefInt: number,
    scan_length_wRefNat: number,

    ctdi: number,
    ctdi_refInt: number,
    ctdi_refNat: number,
    ctdi_wRefInt: number,
    ctdi_wRefNat: number,

    dlp: number,
    dlp_refInt: number,
    dlp_refNat: number,
    dlp_wRefInt: number,
    dlp_wRefNat: number,
}

export class CTEvent extends ORMObject {
    props: CTEventProps

    constructor(props: CTEventData) {
        super();
        this.props = {
            ...props,
            acq_time: props.acq_time ? dayjs(props.acq_time, "DD.MM.YYYY hh:mm:ss") : undefined,
        } as CTEventProps
    }

    public dump(): CTEventData {
        return {
            ...this.props,
            acq_time: this.props.acq_time ? this.props.acq_time.format("DD.MM.YYYY hh:mm:ss") : null,
        };
    }
}

//  MG
interface MGEventData extends EventData {
}

interface MGEventProps extends EventProps {
}

export class MGEvent extends ORMObject {
    props: MGEventProps

    constructor(props: MGEventData) {
        super();
        this.props = {
            ...props,
            acq_time: props.acq_time ? dayjs(props.acq_time, "DD.M.YYYY") : undefined,
        } as MGEventProps
    }

    public dump(): MGEventData {
        return {
            ...this.props,
            acq_time: this.props.acq_time ? this.props.acq_time.format("DD.MM.YYYY") : null,
        };
    }
}

//  NM
interface NMEventData extends EventData {
}

interface NMEventProps extends EventProps {
}

export class NMEvent extends ORMObject {
    props: NMEventProps

    constructor(props: NMEventData) {
        super();
        this.props = {
            ...props,
            acq_time: props.acq_time ? dayjs(props.acq_time, "DD.M.YYYY") : undefined,
        } as NMEventProps
    }

    public dump(): NMEventData {
        return {
            ...this.props,
            acq_time: this.props.acq_time ? this.props.acq_time.format("DD.MM.YYYY") : null,
        };
    }
}

//  PET
interface PETEventData extends EventData {
}

interface PETEventProps extends EventProps {
}

export class PETEvent extends ORMObject {
    props: PETEventProps

    constructor(props: PETEventData) {
        super();
        this.props = {
            ...props,
            acq_time: props.acq_time ? dayjs(props.acq_time, "DD.M.YYYY") : undefined,

        } as PETEventProps
    }

    public dump(): PETEventData {
        return {
            ...this.props,
            acq_time: this.props.acq_time ? this.props.acq_time.format("DD.MM.YYYY") : null,
        };
    }
}

//  RF
interface RFEventData extends EventData {
}

interface RFEventProps extends EventProps {
}

export class RFEvent extends ORMObject {
    props: RFEventProps

    constructor(props: RFEventData) {
        super();
        this.props = {
            ...props,
            acq_time: props.acq_time ? dayjs(props.acq_time, "DD.M.YYYY") : undefined,

        } as RFEventProps
    }

    public dump(): RFEventData {
        return {
            ...this.props,
            acq_time: this.props.acq_time ? this.props.acq_time.format("DD.MM.YYYY") : null,
        };
    }
}

//  XR
interface XREventData extends EventData {
}

interface XREventProps extends EventProps {
}

export class XREvent extends ORMObject {
    props: XREventProps

    constructor(props: XREventData) {
        super();
        this.props = {
            ...props,
            acq_time: props.acq_time ? dayjs(props.acq_time, "DD.M.YYYY") : undefined,

        } as XREventProps
    }

    public dump(): XREventData {
        return {
            ...this.props,
            acq_time: this.props.acq_time ? this.props.acq_time.format("DD.MM.YYYY") : null,
        };
    }
}


//Exam
export interface ExamData extends ORMObjectData {
    device: { uuid: string },

    study_date: string | null,
    study_description: string
    pat_gender: string      //todo make enum
    pat_is_adult: boolean
    pat_bmi_db: number
    pat_height: number
    pat_weight: number
    pat_age: number
    comment: string
    eff_dose: number
    is_deletable?: boolean,
}

interface ExamProps extends ORMObjectProps {
    device: { uuid: string },

    study_date: dayjs.Dayjs,
    study_description: string
    pat_gender: string
    pat_is_adult: boolean
    pat_bmi_db: number
    pat_height: number
    pat_weight: number
    pat_age: number
    comment: string
    eff_dose: number
    is_deletable?: boolean,
}

//  CT
interface CTExamData extends ExamData {

}

interface CTExamProps extends ExamProps {

}

export class CTExam extends ORMObject {
    props: CTExamProps

    constructor(props: CTExamData) {
        super();
        this.props = {
            ...props,
            study_date: props.study_date ? dayjs(props.study_date, "DD.MM.YYYY hh:mm:ss") : undefined,
        } as CTExamProps
    }

    public dump(): CTExamData {
        return {
            ...this.props,
            study_date: this.props.study_date ? this.props.study_date.format("DD.MM.YYYY hh:mm:ss") : null,
        };
    }
}

//  MG
interface MGExamData extends ExamData {
}

interface MGExamProps extends ExamProps {
}

export class MGExam extends ORMObject {
    props: MGExamProps

    constructor(props: MGExamData) {
        super();
        this.props = {
            ...props,
            study_date: props.study_date ? dayjs(props.study_date, "DD.M.YYYY") : undefined,
        } as MGExamProps
    }

    public dump(): MGExamData {
        return {
            ...this.props,
            study_date: this.props.study_date ? this.props.study_date.format("DD.MM.YYYY") : null,
        };
    }
}

//  NM
interface NMExamData extends ExamData {
}

interface NMExamProps extends ExamProps {
}

export class NMExam extends ORMObject {
    props: NMExamProps

    constructor(props: NMExamData) {
        super();
        this.props = {
            ...props,
            study_date: props.study_date ? dayjs(props.study_date, "DD.M.YYYY") : undefined,
        } as NMExamProps
    }

    public dump(): NMExamData {
        return {
            ...this.props,
            study_date: this.props.study_date ? this.props.study_date.format("DD.MM.YYYY") : null,
        };
    }
}

//  PET
interface PETExamData extends ExamData {
}

interface PETExamProps extends ExamProps {
}

export class PETExam extends ORMObject {
    props: PETExamProps

    constructor(props: PETExamData) {
        super();
        this.props = {
            ...props,
            study_date: props.study_date ? dayjs(props.study_date, "DD.M.YYYY") : undefined,

        } as PETExamProps
    }

    public dump(): PETExamData {
        return {
            ...this.props,
            study_date: this.props.study_date ? this.props.study_date.format("DD.MM.YYYY") : null,
        };
    }
}

//  RF
interface RFExamData extends ExamData {
}

interface RFExamProps extends ExamProps {
}

export class RFExam extends ORMObject {
    props: RFExamProps

    constructor(props: RFExamData) {
        super();
        this.props = {
            ...props,
            study_date: props.study_date ? dayjs(props.study_date, "DD.M.YYYY") : undefined,

        } as RFExamProps
    }

    public dump(): RFExamData {
        return {
            ...this.props,
            study_date: this.props.study_date ? this.props.study_date.format("DD.MM.YYYY") : null,
        };
    }
}

//  XR
interface XRExamData extends ExamData {
}

interface XRExamProps extends ExamProps {
}

export class XRExam extends ORMObject {
    props: XRExamProps

    constructor(props: XRExamData) {
        super();
        this.props = {
            ...props,
            study_date: props.study_date ? dayjs(props.study_date, "DD.M.YYYY") : undefined,

        } as XRExamProps
    }

    public dump(): XRExamData {
        return {
            ...this.props,
            study_date: this.props.study_date ? this.props.study_date.format("DD.MM.YYYY") : null,
        };
    }
}

// ManualEntry
interface ManualEntryData {
    device?: DeviceData | null
    month?: string | null
    year?: string | null
    n_events?: number
    n_relRef?: number
    n_param_exc?: number
    n_exc?: number
    n_ctdi_exc?: number
    n_dlp_exc?: number
    n_agd_exc?: number
    n_dfp_exc?: number
    n_inj_exc?: number
    n_ctdi_exc_gt200?: number
    n_dlp_exc_gt200?: number
    n_agd_exc_gt200?: number
    n_dfp_exc_gt200?: number
    n_inj_exc_gt200?: number
    ctdi_max?: number
    dlp_max?: number
    agd_max?: number
    dfp_max?: number
    inj_max?: number
    ctdi_exc_max?: number
    dlp_exc_max?: number
    agd_exc_max?: number
    dfp_exc_max?: number
    inj_exc_max?: number
    n_pat_bmi?: number
    n_phantom_clf_incor?: number
    n_child_clf_incor?: number
    n_noRef?: number
    n_comment_exc?: number
    n_ctdi_thorax?: number
    n_ctdi_thorax_gt80?: number
    ctdi_thorax_max?: number
    n_ctdi_head?: number
    n_ctdi_head_gt120?: number
    ctdi_head_max?: number
}

interface ManualEntryProps extends ORMObjectData{
    device?: Device
    month?: dayjs.Dayjs,
    year?: dayjs.Dayjs,
    n_events?: number
    n_relRef?: number
    n_exc?: number
    n_ctdi_exc?: number
    n_dlp_exc?: number
    n_agd_exc?: number
    n_dfp_exc?: number
    n_inj_exc?: number
    n_ctdi_exc_gt200?: number
    n_dlp_exc_gt200?: number
    n_agd_exc_gt200?: number
    n_dfp_exc_gt200?: number
    n_inj_exc_gt200?: number
    ctdi_max?: number
    dlp_max?: number
    agd_max?: number
    dfp_max?: number
    inj_max?: number
    ctdi_exc_max?: number
    dlp_exc_max?: number
    agd_exc_max?: number
    dfp_exc_max?: number
    inj_exc_max?: number
    n_pat_bmi?: number
    n_phantom_clf_incor?: number
    n_child_clf_incor?: number
    n_noRef?: number
    n_comment_exc?: number
    n_ctdi_thorax?: number
    n_ctdi_thorax_gt80?: number
    ctdi_thorax_max?: number
    n_ctdi_head?: number
    n_ctdi_head_gt120?: number
    ctdi_head_max?: number
    n_dfp_gt20000?: number
    n_dfp_gt50000?: number
}

export class ManualEntry extends ORMObject {
    props: ManualEntryProps

    constructor(props: ManualEntryData) {
        super();
        this.props = {
            ...props,
            device: props.device ? new Device(props.device) : undefined,
            month: props.month ? dayjs(props.month, "DD.M.YYYY") : undefined,
            year: props.year ? dayjs(props.year, "DD.M.YYYY") : undefined,

        } as ManualEntryProps
    }

    public dump(): ManualEntryData {
        return {
            ...this.props,
            device: this.props.device ? {'uuid': this.props.device.props.uuid} : null,
            month: this.props.month ? this.props.month.format("01.MM.YYYY") : null,
            year: this.props.year ? this.props.year.format("01.01.YYYY") : null,
        };
    }
}

interface TodoData extends ORMObjectData {
    comment?: string
    todo_type?: string
    customer?: CustomerData | null
    device?: DeviceData | null
    user?: PersonData | null
}

interface TodoProps extends ORMObjectProps {
    comment?: string
    todo_type?: string
    customer?: Customer
    device?: Device
    user?: Person
}

export class Todo extends ORMObject {

    props: TodoProps

    constructor(props: TodoData) {
        super();
        this.props = {
            ...props,
            device: props.device ? new Device(props.device) : undefined,
            customer: props.customer ? new Customer(props.customer) : undefined,
            user: props.user ? new Person(props.user) : undefined,

        } as TodoProps
    }

    public dump(): TodoData {
        return {
            ...this.props,
            device: this.props.device ? {'uuid': this.props.device.props.uuid} : null,
            customer: this.props.customer ? {'uuid': this.props.customer.props.uuid} : null,
            user: this.props.user ? {'uuid': this.props.user.props.uuid} : null
        };
    }

}


